// React
import { memo, useCallback, useMemo, useState } from "react";

// Helpers
import { classNames, transformDateToLocalDateString } from "helpers";

// Servisni sloj
import { ANNUAL_DISCOUNT_KEY_CODE } from "services/api/subscriptions/consts";
import { PricingTierFormValues } from "services/api/pricing/types";

// Lokalne komponente
import PricingPackSummary from "./components/PricingPackSummary/PricingPackSummary";
import { PricingPackTierRow } from "./components/PricingPackSummary/types";

// Tipovi
import { PricingSummaryCardProps } from "./types";

/**
 * Raspon razreda u riječima.
 *
 * Zatvoreni razredi se pišu kao „do N vozila" jer stoje jedan ispod drugoga i čitaju se kao
 * ljestvica; donja granica je onda već vidljiva iz retka iznad.
 */
const tierLabel = (tier: PricingTierFormValues): string =>
  tier.toVehicle === null
    ? `od ${tier.fromVehicle} vozila`
    : `do ${tier.toVehicle} vozila`;

/**
 * Razred po razred, s cijenom po vozilu unutar raspona. Stopa je udio pune cijene, pa je
 * popust ono što do sto posto nedostaje.
 */
const tierRows = (
  tiers: PricingTierFormValues[],
  unitPrice: number | null
): PricingPackTierRow[] => {
  if (unitPrice === null) return [];

  return tiers.map((tier) => ({
    label: tierLabel(tier),
    perVehicle: unitPrice * ((tier.ratePercent ?? 0) / 100),
    discountPercent: Math.round(100 - (tier.ratePercent ?? 0)),
  }));
};

/** Popust na godišnje plaćanje ide na pretplatu, pa i na cijenu po vozilu. */
const withAnnualDiscount = (price: number | null, discountPercent: number): number | null =>
  price === null ? null : price * (1 - discountPercent / 100);

/**
 * Lijeva ploha čarobnjaka. Puni se dok se cjenik upisuje: datum s prvog koraka, paketi s
 * drugog, a razrada po razredima čim ljestvica postane potpuna.
 */
const PricingSummaryCard: React.FC<PricingSummaryCardProps> = (
  props: PricingSummaryCardProps
) => {
  const { formValues, packs } = props;

  const packNameByCode = useMemo(
    () => new Map(packs.map((pack) => [pack.code, pack.name])),
    [packs]
  );

  const isLadderReady =
    formValues.priceTiers.length > 0 &&
    formValues.priceTiers.every(
      (tier) => tier.fromVehicle !== null && tier.ratePercent !== null
    );

  // Redoslijed unosa ne mora pratiti ljestvicu, a razredi se čitaju odozdo prema gore.
  const orderedTiers = useMemo(
    () =>
      isLadderReady
        ? [...formValues.priceTiers].sort((a, b) => a.fromVehicle! - b.fromVehicle!)
        : [],
    [formValues.priceTiers, isLadderReady]
  );

  /*----------------------------------------------------------------
      MJESEČNO / GODIŠNJE

      Prekidač ne mijenja cjenik, samo pogled na njega: popust na godišnje plaćanje upisuje se
      na zadnjem koraku, a ovdje se odmah vidi što s njim ispada.
  ----------------------------------------------------------------*/

  const [isAnnual, setIsAnnual] = useState<boolean>(false);

  const annualDiscountPercent = formValues.values[ANNUAL_DISCOUNT_KEY_CODE] ?? 0;
  const appliedDiscount = isAnnual ? annualDiscountPercent : 0;

  const handleMonthlyClick = useCallback(() => {
    setIsAnnual(false);
  }, []);

  const handleAnnualClick = useCallback(() => {
    setIsAnnual(true);
  }, []);

  return (
    <div className="bg-blue-600 h-full w-full min-w-0 max-w-full overflow-hidden flex flex-col items-center justify-center p-8">
      <div className="text-center">
        <h1 className="text-4xl font-bold tracking-tight text-white">
          {formValues.note || "Cjenik"}
        </h1>

        {/* Datum kao pilula: na plavom se čita kao oznaka, a ne kao još jedan redak teksta. */}
        <div className="mt-4 inline-block rounded-full bg-white/15 px-4 py-1.5 text-sm text-white">
          {formValues.validFrom
            ? `Vrijedi od ${transformDateToLocalDateString(new Date(formValues.validFrom))}`
            : "Odaberite od kada cjenik vrijedi"}
        </div>
      </div>

      {/* Prekidač se nudi samo kad popust postoji; s nulom bi obje strane davale isto. */}
      {annualDiscountPercent > 0 && (
        <div className="mt-7 inline-flex items-center gap-1 rounded-full bg-white/15 p-1">
          <button
            type="button"
            onClick={handleMonthlyClick}
            className={classNames(
              "rounded-full px-5 py-1.5 text-sm transition-colors",
              isAnnual
                ? "text-blue-50 hover:text-white"
                : "bg-white font-semibold text-slate-900 shadow-sm"
            )}
          >
            Mjesečno
          </button>

          <button
            type="button"
            onClick={handleAnnualClick}
            className={classNames(
              "rounded-full px-5 py-1.5 text-sm transition-colors",
              isAnnual
                ? "bg-white font-semibold text-slate-900 shadow-sm"
                : "text-blue-50 hover:text-white"
            )}
          >
            Godišnje{" "}
            <span className={classNames("font-medium", isAnnual ? "text-green-600" : "text-green-300")}>
              −{annualDiscountPercent.toFixed(2).replace(".", ",").replace(",00", "")} %
            </span>
          </button>
        </div>
      )}

      <div className="mt-8 w-full min-w-0 max-w-full overflow-x-auto overflow-y-hidden">
        {formValues.combinations.length === 0 ? (
          <p className="text-blue-100 text-sm text-center">
            Paketi se pojavljuju ovdje čim im na koraku „Cijena po vozilu" dodate cijenu.
          </p>
        ) : (
          /*
            Kartice dijele jednu plohu i razdvaja ih tanka crta. `w-max mx-auto` centrira ih
            dok stanu, a kad ih ima previše ploha se klizne umjesto da se odsiječe s lijeve
            strane, kako bi se dogodilo s `justify-center`.
          */
          <div className="w-max mx-auto flex divide-x divide-slate-200 bg-white rounded-md overflow-hidden shadow-2xl ring-1 ring-black/5 snap-x snap-mandatory">
            {formValues.combinations.map((combination, index) => (
              <PricingPackSummary
                key={index}
                packNames={combination.packCodes.map(
                  (code) => packNameByCode.get(code) ?? code
                )}
                description={combination.description}
                unitPrice={withAnnualDiscount(combination.price, appliedDiscount)}
                rows={tierRows(
                  orderedTiers,
                  withAnnualDiscount(combination.price, appliedDiscount)
                )}
                isAnnual={isAnnual}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(PricingSummaryCard);
